/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 外部工具(外呼 / 邮件 / 数据获客)—— 2026-09-17 从获客舱首页**降级**到这里。
 * ═══════════════════════════════════════════════════════════════════════════
 * 契约 §8:三张外链**保留,但不再与获客能力平级摆首页**。它们变成商机卡上动作按钮的
 * 落点 —— 该打电话 → 去外呼系统(`?tool=waihu`);该发邮件 → 去邮件系统(`?tool=email`)。
 * 这个页面是那两条落点的着陆处,外加一个"我就想自己进去看看"的直达入口。
 *
 * ★外链三卡沿用 GEO-CARDS 反代机制(标记名保留 `GEO-CARDS`,与 nginx.conf / vite.config.ts 对应):
 *   - embed 前缀必须与 nginx.conf / vite.config.ts 里的 location 一一对应(xiaobang/oldgeo/yg400)。
 *     2026-09-21 起数据获客改走 xiaobang(与外呼获客同一个链接),本页不再引用 yg400;
 *     nginx.conf 里 yg400 的 location 与两条 307 安全网暂留未删(回滚只需改回本文件)。
 *   - 为什么反代而不直接 iframe 对方地址:跨域 iframe 里对方 session cookie(SameSite=Lax)建不起来,
 *     登录页出得来但登不进去;反代成同源后 cookie 变第一方(2026-08-20 真账号验证)。
 *   - 「登得进去」≠「进去以后能用」:三方 app 在 sub_filter 改不到的位置写根相对路径会逃出代理前缀,
 *     兜底靠 nginx.conf 里四条 307「安全网」location,别当可有可无的优化删掉。
 *   - 能不能渲染由【边缘 nginx 的安全头】决定(frame-ancestors 'self' 才放行),不在本仓,
 *     测试机是 /etc/nginx/conf.d/gscq-test.conf 手工段(带 GEO-CARDS 标记)。
 *   - 整体移除时的完整清单沿用原 GeoCardsPage 八条,现分布在:本文件、App.tsx、ProtectedRoute、
 *     nginx.conf、vite.config.ts(全部可 grep `GEO-CARDS` 找齐)。
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { useCallback, useEffect, useState, type KeyboardEvent } from "react";
import { ArrowLeft, ArrowRight, ExternalLink, Headset, Mail, Radar } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { useBrand } from "@/shared/brand";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { cn } from "@/shared/lib/utils";

import {
  GEO_ENTRY_REASON_TEXT,
  geoEntryService,
  type GeoEntryReason,
} from "../api/geoEntryService";
import { isExternalToolEnabled } from "../model/externalToolModules";
import { AcqShell } from "./AcqShell";

interface ToolEntry {
  key: string;
  title: string;
  description: string;
  icon: LucideIcon;
  /** iframe 反代地址(GEO-CARDS);新窗口型卡片没有,「进入」直接新开标签页。 */
  embed?: string;
  /**
   * 需要**先向后端换地址**再内嵌的卡(目前只有 GEO 免密)。有它时 embed 不参与,地址由后端现算。
   * 为什么不能只放一个静态 embed:进入地址带着该账号的专属 token,只有后端知道当前登录人对应哪个。
   */
  entry?: "geo";
  href: string;
  /** 商机卡上哪个动作会把人送到这儿(契约 §8)。 */
  from?: string;
}

// 外呼获客与数据获客共用这一组地址(2026-09-21 老板:数据获客改成跟外呼获客一样的链接)。
// 写成一份,两张卡不会各改各的漂开。
const XIAOBANG_LINK = {
  href: "https://saas.xiaobangcall.fun/admin/index/index.html",
  embed: "/proxy/xiaobang/admin/index/index.html",
} as const;

const TOOLS: ToolEntry[] = [
  {
    key: "waihu",
    title: "外呼获客",
    description: "机器人外呼初筛意向客户，把销售时间留给真正值得谈的人",
    icon: Headset,
    ...XIAOBANG_LINK,
    from: "商机卡上「该打电话」的落点",
  },
  {
    key: "email",
    title: "邮件获客",
    description: "海量群发 + 意向数据回收，触达与回收在同一条链路里",
    icon: Mail,
    href: "http://8.138.227.56:9003/",
    embed: "/proxy/oldgeo/",
    from: "商机卡上「该发邮件」的落点",
  },
  {
    key: "geo",
    title: "数据获客",
    // 修复波 0926 P9:旧描述是 GEO 口吻(「把品牌建设进 AI 大模型的答案里」),而 0921 起这张卡
    // 链的就是外呼那套系统(XIAOBANG_LINK),与 GEO 雷达无关 —— 只写中性的实际去处。
    description: "进入数据获客系统（与外呼系统是同一个入口）",
    icon: Radar,
    ...XIAOBANG_LINK,
  },
  {
    key: "geoAdmin",
    title: "GEO管理系统",
    description: "进入 GEO 后台管理系统",
    icon: Radar,
    // 同源代理避免浏览器拦截第三方会话 cookie。
    entry: "geo",
    href: "https://geo.xbgeo.cn/",
  },
];

/** 与 nginx.conf / vite.config.ts 的 `/proxy/geo/` 一一对应(改一处必须改三处)。 */
const GEO_PROXY_PREFIX = "/proxy/geo/";

/**
 * 后端给的是对方域名的**绝对**地址(它的 base-url 是后端配置),这里换成同源反代路径再塞进 iframe。
 *
 * <p>为什么不硬编码对方域名做字符串替换:后端 base-url 是可配的(联调/生产可能不同域名),按 URL 结构取
 * path+query 就不依赖任何具体域名。
 */
export function toProxyUrl(absUrl: string, prefix = GEO_PROXY_PREFIX): string {
  try {
    const u = new URL(absUrl);
    return prefix + u.pathname.replace(/^\//, "") + u.search;
  } catch {
    return absUrl; // 形状不对就原样用,别把地址拼坏
  }
}

export default function ExternalToolsPage() {
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState<ToolEntry | null>(null);
  // GEO 免密:地址要现向后端换(带该账号 token),所以选中之后还有一段异步。
  const [entryUrl, setEntryUrl] = useState<string | null>(null);
  const [entryReason, setEntryReason] = useState<GeoEntryReason | null>(null);
  const [entryLoading, setEntryLoading] = useState(false);
  // OEM 贴牌(§2.4):按品牌模块开关过滤(直营全开 = 三张照旧)。
  const brand = useBrand();
  const tools = TOOLS.filter((t) => isExternalToolEnabled(t.key, brand.modules));

  /** 点一张卡:免密卡先换地址,静态内嵌卡直接进,新窗口卡开标签页。 */
  const openTool = useCallback(async (tool: ToolEntry) => {
    if (!tool.entry && !tool.embed) {
      window.open(tool.href, "_blank", "noopener,noreferrer");
      return;
    }
    setSelected(tool);
    setEntryUrl(null);
    setEntryReason(null);
    if (!tool.entry) {
      return; // 静态内嵌卡:embed 就够了
    }
    setEntryLoading(true);
    try {
      const r = await geoEntryService.entry();
      if (r.available && r.url) {
        setEntryUrl(toProxyUrl(r.url));
      } else {
        setEntryReason(r.reason ?? "NO_ACCOUNT");
      }
    } catch {
      setEntryReason("ERROR");
    } finally {
      setEntryLoading(false);
    }
  }, []);

  // 商机卡的主按钮走 `?tool=waihu` 过来 —— 直接开那一个,不让用户再挑一次。
  const wanted = params.get("tool");
  useEffect(() => {
    if (!wanted) return;
    const hit = TOOLS.find((t) => t.key === wanted);
    if (hit && isExternalToolEnabled(hit.key, brand.modules) && (hit.embed || hit.entry)) {
      void openTool(hit);
    }
  }, [wanted, brand.modules, openTool]);

  const close = () => {
    setSelected(null);
    setEntryUrl(null);
    setEntryReason(null);
    if (params.has("tool")) {
      params.delete("tool");
      setParams(params, { replace: true });
    }
  };

  // 外链内嵌视图(GEO-CARDS 机制原样保留)。
  if (selected) {
    const src = selected.entry ? entryUrl : selected.embed;
    return (
      <div className="flex h-[calc(100vh-10rem)] min-h-[480px] flex-col">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={close}
            className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            返回获客舱
          </button>
          <span className="font-semibold">{selected.title}</span>
          <a
            href={selected.href}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-primary"
          >
            <ExternalLink className="h-4 w-4" />
            新窗口打开
          </a>
        </div>
        {entryReason ? (
          <EntryNotice text={GEO_ENTRY_REASON_TEXT[entryReason]} />
        ) : entryLoading || !src ? (
          <div className="grid flex-1 place-items-center rounded-lg border bg-background text-sm text-muted-foreground">
            正在进入…
          </div>
        ) : (
          <iframe
            src={src}
            title={selected.title}
            className="w-full flex-1 rounded-lg border bg-background"
          />
        )}
      </div>
    );
  }

  return (
    <AcqShell
      subtitle={
        tools.length === TOOLS.length
          ? "这些是外部系统 —— 商机卡上点「去打电话 / 去发邮件」就会落到这里"
          : tools.length > 0
            ? "这些是外部系统 —— 商机卡上的对应动作会落到这里"
            : "当前没有开通外部工具"
      }
    >
      <div className="cardwall">
        <div className="cardwall-cap">
          {tools.map((tool) => (
            <ToolCard key={tool.key} tool={tool} onOpen={() => void openTool(tool)} />
          ))}
        </div>
      </div>
    </AcqShell>
  );
}

function ToolCard({ tool, onOpen }: { tool: ToolEntry; onOpen: () => void }) {
  const Icon = tool.icon;
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={onKeyDown}
      className={cn(
        "group relative flex cursor-pointer flex-col overflow-hidden border bg-card text-left",
        CARD_WALL_RADIUS
      )}
    >
      <div className="grid h-24 w-full place-items-center border-b bg-muted/30">
        <Icon className="h-10 w-10 text-primary/90" strokeWidth={1.4} aria-hidden />
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <h3 className="text-sm font-[650] tracking-[-0.012em]">{tool.title}</h3>
        <p className="line-clamp-2 text-2xs leading-[1.62] text-muted-foreground">
          {tool.description}
        </p>
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-border/60 px-3 py-2">
        <span className="min-w-0 truncate text-2xs text-muted-foreground">
          {tool.from ?? "外部系统"}
        </span>
        <span className="inline-flex shrink-0 items-center gap-0.5 text-2xs font-semibold text-primary">
          进入
          <ArrowRight className="h-3 w-3" />
        </span>
      </div>
    </div>
  );
}

export function EntryNotice({ text }: { text: string }) {
  return (
    <div className="grid flex-1 place-items-center rounded-lg border bg-background">
      <div className="max-w-md px-6 text-center">
        <p className="text-sm text-foreground">{text}</p>
        <a
          href="https://geo.xbgeo.cn/"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-primary"
        >
          <ExternalLink className="h-3.5 w-3.5" />去 GEO 官方站点
        </a>
      </div>
    </div>
  );
}
